import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "card" }
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "card-header border-0" }, [
  /*#__PURE__*/_createElementVNode("div", { class: "card-title" }, "调用量")
], -1)
const _hoisted_3 = { class: "card-body pt-10" }
const _hoisted_4 = { class: "dataTables_wrapper dt-bootstrap4 no-footer" }
const _hoisted_5 = {
  class: "table-responsive",
  style: {"min-height":"400px"}
}

import {
  ref,
  onMounted,
  reactive,
  watch,
  computed,
  createApp,
} from "vue";
import TooltipContent from "./TooltipContent.vue";
import ApiService from "@/core/services/ApiService";
import { getCSSVariableValue } from "@/assets/ts/_utils";
import { parseTime } from "@/utils";

// 表格载入延迟

export default /*#__PURE__*/_defineComponent({
  props: {
  filters: {
    type: Object,
    default: () => ({}),
  },
},
  setup(__props) {

const props = __props

const loading = ref<boolean>(false);
const chart = ref<HTMLElement | null>(null);

const filters = computed(() => props.filters as any);
const dateType = computed(() => filters.value.dateType || "day");

const labelColor = getCSSVariableValue("--bs-" + "gray-800");
const strokeColor = getCSSVariableValue("--bs-" + "gray-300");
const baseColor = getCSSVariableValue("--bs-" + "primary");
const compareColor = "#89D3FB"; // 使用主色调的淡化版 (浅蓝色) 使对比曲线颜色更淡
const lightColor = getCSSVariableValue("--bs-light-" + "primary");
const compareLightColor = "rgba(137, 211, 251, 0.2)"; // 对应的浅色背景

const originalCurrentData = ref<any[]>([]);
const originalCompareData = ref<any[]>([]);

const series = ref<Array<any>>([]);
const options = reactive({
  chart: {
    type: "area",
    fontFamily: "inherit",
    toolbar: {
      show: true,
    },
  },
  plotOptions: {},
  legend: {
    show: true,
    position: "top",
  },
  dataLabels: {
    enabled: false,
  },
  stroke: {
    curve: "smooth",
    show: true,
    width: 3,
    colors: [baseColor, compareColor],
    dashArray: [0, 0],
  },
  fill: {
    type: "gradient",
    gradient: {
      shadeIntensity: 1,
      opacityFrom: 0.45,
      opacityTo: 0.05,
      stops: [20, 100, 100, 100],
    },
  },
  xaxis: {
    type: "category",
    categories: [] as any[],
    labels: {
      show: true,
      rotate: -45,
      style: {
        colors: labelColor,
      },
    },
    tooltip: {
      enabled: false,
    },
  },
  yaxis: {
    min: 0,
    forceNiceScale: true,
    labels: {
      style: {
        colors: labelColor,
      },
    },
  },
  colors: [baseColor, compareColor],
  markers: {
    size: 4,
    colors: [baseColor, compareColor],
    strokeColors: "#fff",
    strokeWidth: 2,
    hover: {
      size: 6,
    },
  },
  tooltip: {
    shared: true,
    intersect: false,
    // y formatter is now the primary formatter
    y: {
      formatter: (value, { seriesIndex, dataPointIndex }) => {
        if (!value) return `0 次`;
        return `${value.toLocaleString()} 次`;
      },
    },
  },
});

onMounted(() => {
  loadData();
});

watch(
  filters,
  (val) => {
    loadData();
  },
  { deep: true },
);

// 数据载入
const loadData = async () => {
  if (loading.value) return;
  loading.value = true;

  try {
    const currentParams = { ...filters.value, dateType: dateType.value };
    const requests = [ApiService.get("m/api/digram", currentParams)];

    if (
      filters.value["compare_start_date"] &&
      filters.value["compare_stop_date"]
    ) {
      const compareParams = {
        ...filters.value,
        start_date: filters.value["compare_start_date"],
        stop_date: filters.value["compare_stop_date"],
        compare_start_date: undefined,
        compare_stop_date: undefined,
        dateType: dateType.value,
      };
      requests.push(ApiService.get("m/api/digram", compareParams));
    } else {
      // Ensure compare data is cleared when not in use
      originalCompareData.value = [];
    }

    const responses = await Promise.all(requests);
    const currentData = responses[0].data || [];
    const compareData = responses[1]?.data || [];

    // Populate the component-level refs
    originalCurrentData.value = [...currentData];
    if (responses[1]) {
      originalCompareData.value = [...compareData];
    }

    const hasComparison = compareData.length > 0;
    const categories = currentData.map((d: any) => d.dt);
    const maxLength = Math.max(currentData.length, compareData.length);

    const newSeries: any[] = [];
    if (hasComparison) {
      newSeries.push({
        name: `当前 (${filters.value["start_date"]} ~ ${filters.value["stop_date"]})`,
        data: Array.from(
          { length: maxLength },
          (_, i) => currentData[i]?.cnt || 0,
        ),
      });
      newSeries.push({
        name: `对比 (${filters.value["compare_start_date"]} ~ ${filters.value["compare_stop_date"]})`,
        data: Array.from(
          { length: maxLength },
          (_, i) => compareData[i]?.cnt || 0,
        ),
      });
    } else {
      newSeries.push({
        name: "调用量",
        data: currentData.map((d: any) => d.cnt),
      });
    }

    series.value = newSeries;
    options.xaxis.categories = categories;
    options.legend.show = hasComparison;
    options.stroke.dashArray = hasComparison ? [0, 5] : [0];

    // Custom tooltip using Vue component for all modes
    options.tooltip = {
      shared: true,
      intersect: false,
      custom: ({ series, seriesIndex, dataPointIndex, w }) => {
        if (dataPointIndex === -1 || !series[0]) return "";

        const props = {
          title: "时间",
          colors: w.globals.colors,
          currentVal: series[0][dataPointIndex] || 0,
          currentDt: originalCurrentData.value[dataPointIndex]?.dt || "-",
          compareVal:
            series[1] && series[1][dataPointIndex] !== undefined
              ? series[1][dataPointIndex]
              : null,
          compareDt: originalCompareData.value[dataPointIndex]?.dt || "-",
        };

        const container = document.createElement("div");
        const app = createApp(TooltipContent, props);
        app.mount(container);
        const html = container.innerHTML;
        app.unmount();
        return html;
      },
    } as any;

    if ((chart as any).value) {
      (chart as any).value.updateOptions(options);
      (chart as any).value.updateSeries(series.value);
    }
  } catch (error) {
    console.error("加载监控图表数据失败:", error);
  } finally {
    loading.value = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_apexchart = _resolveComponent("apexchart")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _hoisted_2,
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("div", _hoisted_4, [
        _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_5, [
          _createVNode(_component_apexchart, {
            ref_key: "chart",
            ref: chart,
            type: "area",
            height: "400",
            options: _unref(options),
            series: series.value,
            class: "mixed-widget-7-chart card-rounded-bottom"
          }, null, 8, ["options", "series"])
        ])), [
          [_directive_loading, loading.value]
        ])
      ])
    ])
  ]))
}
}

})